import { useEffect, useState } from 'react';
import { useFormik } from 'formik';

import { useDispatch, useSelector } from 'react-redux';
import { errorHelper } from '../../utils/tools';
import { validation, formValues, CLIENT_CATEGORIES } from './validationSchema';

import { addClient, fetchClients, addBranch } from '../../store/actions/client';
import { getTenant } from '../../store/actions/tenant';
import { resetCompanySearch } from '../../store/reducers/companySearch';

import { Button, Form, Row, Col } from 'react-bootstrap';
import CreatableSelect from 'react-select/creatable';

import CompanySearch from '../companySearch';
import { COUNTRIES } from '../../utils/constants/countries';

const AddClient = ({ isVisible, onClose, onSuccess, initialCategories }) => {

    const dispatch = useDispatch();
    const currentCompany = useSelector(state => state.companySearch.current);
    const branchId = useSelector(state => state.branch.current);

    const [isAbroad, setIsAbroad] = useState(false);
    const [createdClient, setCreatedClient] = useState(null);
    const [showAddBranch, setShowAddBranch] = useState(false);
    const [newBranch, setNewBranch] = useState({
        name: '',
        address: { street: '', zip: '', city: '', country: 'Slovensko' }
    });
    const [branchErrors, setBranchErrors] = useState({});

    const getInitialValues = () => ({
        ...formValues,
        categories: initialCategories && initialCategories.length ? initialCategories : formValues.categories
    });

    const formik = useFormik({
        initialValues: getInitialValues(),
        validationSchema: validation(isAbroad),
        onSubmit: (values) => handleSubmit(values)
    });

    const toggleCategory = (value) => {
        const current = formik.values.categories || [];
        const next = current.includes(value) ? current.filter(c => c !== value) : [...current, value];
        formik.setFieldValue('categories', next);
    };

    const handleAbroadToggle = (checked) => {
        setIsAbroad(checked);
        formik.setValues({
            ...formik.values,
            isAbroad: checked,
            address: {
                ...formik.values.address,
                country: checked ? '' : 'Slovensko',
            }
        }, false);
        formik.setTouched({}, false);
        setNewBranch(prev => ({
            ...prev,
            address: { ...prev.address, country: checked ? '' : 'Slovensko' }
        }));
        if (checked) dispatch(resetCompanySearch());
    };

    useEffect(() => {
        if (!isVisible) return;
        formik.resetForm({ values: getInitialValues() });
        setCreatedClient(null);
        setIsAbroad(false);
        setShowAddBranch(false);
        setNewBranch({ name: '', address: { street: '', zip: '', city: '', country: 'Slovensko' } });
        setBranchErrors({});
        dispatch(resetCompanySearch());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]);

    useEffect(() => {
        if (isAbroad) return;
        formik.setFieldValue('companyName', currentCompany.companyName || '');
        formik.setFieldValue('address.street', currentCompany.street || '');
        formik.setFieldValue('address.zip', currentCompany.zip || '');
        formik.setFieldValue('address.city', currentCompany.city || '');
        formik.setFieldValue('address.country', currentCompany.coutry || 'Slovensko');
        formik.setFieldValue('identificationNumber', currentCompany.identificationNumber || '');
        formik.setFieldValue('vatId', currentCompany.vatId || '');
        formik.setFieldValue('vatRegNo', currentCompany.vatRegNo || '');
    }, [currentCompany]);

    const handleSubmit = async (values) => {
        try {
            const client = await dispatch(addClient(values)).unwrap();
            await dispatch(getTenant(branchId)).unwrap();
            await dispatch(fetchClients()).unwrap();
            formik.resetForm();
            setCreatedClient(client);
            setNewBranch(prev => ({ ...prev, address: { ...prev.address, country: client.address.country } }));
        } catch (error) {
            console.error('Error adding client:', error);
        }
    };

    const validateBranch = (branch) => {
        const errors = {};
        if (!branch.name || branch.name.trim().length < 1) errors.name = 'Názov je povinný.';
        if (!branch.address.street || branch.address.street.trim().length < 1) errors.street = 'Ulica je povinná.';
        if (isAbroad) {
            if (!branch.address.zip || branch.address.zip.trim().length < 3) errors.zip = 'PSČ musí mať aspoň 3 znaky.';
            else if (branch.address.zip.trim().length > 20) errors.zip = 'PSČ môže mať maximálne 20 znakov.';
        } else {
            if (!branch.address.zip || !/^\d{5}$/.test(branch.address.zip)) errors.zip = 'PSČ musí mať 5 číslic.';
        }
        if (!branch.address.city || branch.address.city.trim().length < 2) errors.city = 'Mesto je povinné.';
        if (!branch.address.country || branch.address.country.trim().length < 1) errors.country = 'Krajina je povinná.';
        return errors;
    };

    const handleAddBranch = async () => {
        const errors = validateBranch(newBranch);
        if (Object.keys(errors).length > 0) {
            setBranchErrors(errors);
            return;
        }
        setBranchErrors({});
        const updated = await dispatch(addBranch({ clientId: createdClient._id, name: newBranch.name, address: newBranch.address })).unwrap();
        setCreatedClient(updated);
        setShowAddBranch(false);
        setNewBranch({ name: '', address: { street: '', zip: '', city: '', country: createdClient.address.country } });
    };

    const handleClose = () => {
        if (onSuccess && createdClient) {
            onSuccess(createdClient._id);
        }
        setCreatedClient(null);
        setIsAbroad(false);
        setShowAddBranch(false);
        setNewBranch({ name: '', address: { street: '', zip: '', city: '', country: 'Slovensko' } });
        setBranchErrors({});
        formik.resetForm();
        dispatch(resetCompanySearch());
        onClose();
    };

    if (createdClient) {
        return (
            <div>
                <h2>Pridať odberateľa/dodávateľa</h2>
                <hr />

                <p className="text-muted mb-3">
                    <strong>{createdClient.companyName}</strong> bol úspešne vytvorený. Môžete pridať prevádzkarne alebo zatvoriť.
                </p>

                <div className="d-flex justify-content-between align-items-center mb-2">
                    <span><strong>Prevádzkarne</strong></span>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setShowAddBranch(!showAddBranch)}
                    >
                        {showAddBranch ? 'Zrušiť' : 'Pridať prevádzkareň'}
                    </Button>
                </div>

                {showAddBranch && (
                    <div className="border rounded p-3 mb-3">
                        <Form.Group className="mb-2">
                            <Form.Label>Názov prevádzkarne*</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="napr. Košice - Moldavská"
                                value={newBranch.name}
                                onChange={e => setNewBranch({ ...newBranch, name: e.target.value })}
                                isInvalid={!!branchErrors.name}
                            />
                            <Form.Control.Feedback type="invalid">{branchErrors.name}</Form.Control.Feedback>
                        </Form.Group>
                        <Row>
                            <Col>
                                <Form.Group className="mb-2">
                                    <Form.Label>Ulica*</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Ulica a číslo"
                                        value={newBranch.address.street}
                                        onChange={e => setNewBranch({ ...newBranch, address: { ...newBranch.address, street: e.target.value } })}
                                        isInvalid={!!branchErrors.street}
                                    />
                                    <Form.Control.Feedback type="invalid">{branchErrors.street}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-2">
                                    <Form.Label>PSČ*</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder={isAbroad ? 'PSČ / ZIP' : '00000'}
                                        value={newBranch.address.zip}
                                        onChange={e => setNewBranch({ ...newBranch, address: { ...newBranch.address, zip: e.target.value } })}
                                        isInvalid={!!branchErrors.zip}
                                    />
                                    <Form.Control.Feedback type="invalid">{branchErrors.zip}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Form.Group className="mb-2">
                                    <Form.Label>Mesto*</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Mesto"
                                        value={newBranch.address.city}
                                        onChange={e => setNewBranch({ ...newBranch, address: { ...newBranch.address, city: e.target.value } })}
                                        isInvalid={!!branchErrors.city}
                                    />
                                    <Form.Control.Feedback type="invalid">{branchErrors.city}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-2">
                                    <Form.Label>Krajina*</Form.Label>
                                    {isAbroad ? (
                                        <>
                                            <CreatableSelect
                                                options={COUNTRIES}
                                                value={newBranch.address.country ? { value: newBranch.address.country, label: newBranch.address.country } : null}
                                                onChange={option => setNewBranch({ ...newBranch, address: { ...newBranch.address, country: option ? option.value : '' } })}
                                                placeholder="Vyhľadať alebo vybrať krajinu..."
                                                formatCreateLabel={val => `Použiť: "${val}"`}
                                                isClearable
                                                noOptionsMessage={() => 'Žiadne výsledky'}
                                                styles={{
                                                    control: (base, state) => ({
                                                        ...base,
                                                        borderColor: branchErrors.country ? '#dc3545' : state.isFocused ? '#86b7fe' : '#ced4da',
                                                        boxShadow: branchErrors.country ? '0 0 0 0.25rem rgba(220,53,69,.25)' : state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,.25)' : 'none',
                                                        '&:hover': { borderColor: branchErrors.country ? '#dc3545' : '#86b7fe' }
                                                    })
                                                }}
                                            />
                                            {branchErrors.country && (
                                                <div className="invalid-feedback d-block">{branchErrors.country}</div>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <Form.Control
                                                type="text"
                                                placeholder="Slovensko"
                                                value={newBranch.address.country}
                                                onChange={e => setNewBranch({ ...newBranch, address: { ...newBranch.address, country: e.target.value } })}
                                                isInvalid={!!branchErrors.country}
                                            />
                                            <Form.Control.Feedback type="invalid">{branchErrors.country}</Form.Control.Feedback>
                                        </>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                        <Button variant="primary" size="sm" onClick={handleAddBranch}>
                            Uložiť prevádzkareň
                        </Button>
                    </div>
                )}

                {!showAddBranch && createdClient.branches?.length === 0 && (
                    <p className="text-muted small">Žiadne prevádzkarne. Sídlo firmy sa použije ako predvolená adresa.</p>
                )}

                {Array.isArray(createdClient.branches) && createdClient.branches.map(branch => (
                    <div key={branch._id} className="d-flex justify-content-between align-items-start border rounded p-2 mb-2">
                        <div>
                            <strong>{branch.name}</strong>
                            <div className="text-muted small">
                                {branch.address.street}, {branch.address.zip} {branch.address.city}
                            </div>
                        </div>
                    </div>
                ))}

                <Row className="mt-4">
                    <hr />
                    <Col className="d-flex justify-content-end">
                        <Button className="button-custom-large" variant="primary" size="lg" onClick={handleClose}>
                            Zatvoriť
                        </Button>
                    </Col>
                </Row>
            </div>
        );
    }

    return (
        <div>
            <h2>Pridať odberateľa/dodávateľa</h2>
            <hr />

            <Form onSubmit={formik.handleSubmit}>

                <div className="p-3 mb-4 rounded" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                    <Form.Check
                        type="switch"
                        id="isAbroad"
                        label={
                            <span>
                                Zahraničný odberateľ/dodávateľ
                                <span className={`badge ms-2 ${isAbroad ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                    {isAbroad ? 'Zahraničný' : 'Slovenský'}
                                </span>
                            </span>
                        }
                        checked={isAbroad}
                        onChange={e => handleAbroadToggle(e.target.checked)}
                        className="mb-0"
                    />
                    {isAbroad && (
                        <small className="text-muted mt-1 d-block">
                            Pre zahraničné firmy nie je dostupné automatické vyhľadávanie v obchodnom registri.
                        </small>
                    )}
                </div>

                <Form.Group className="mb-4">
                    <Form.Label>Kategória*</Form.Label>
                    <div className="d-flex gap-3">
                        {CLIENT_CATEGORIES.map(({ value, label }) => (
                            <Form.Check
                                key={value}
                                type="checkbox"
                                id={`category-${value}`}
                                label={label}
                                checked={(formik.values.categories || []).includes(value)}
                                onChange={() => toggleCategory(value)}
                            />
                        ))}
                    </div>
                    {formik.touched.categories && formik.errors.categories && (
                        <div className="invalid-feedback d-block">{formik.errors.categories}</div>
                    )}
                </Form.Group>

                <Row>
                    <Col>
                        {!isAbroad ? (
                            <CompanySearch formik={formik} />
                        ) : (
                            <Form.Group className="mb-3" controlId="formBasicCompanyName">
                                <Form.Label>Názov spoločnosti*</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Zadajte názov spoločnosti"
                                    {...formik.getFieldProps('companyName')}
                                    isInvalid={errorHelper(formik, 'companyName').error}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errorHelper(formik, 'companyName').helperText}
                                </Form.Control.Feedback>
                            </Form.Group>
                        )}

                        <Form.Group className="mb-3" controlId="formBasicIdentificationNumber">
                            <Form.Label>{isAbroad ? 'Registračné číslo*' : 'IČO*'}</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={isAbroad ? 'Zadajte registračné číslo' : 'Zadajte IČO'}
                                {...formik.getFieldProps('identificationNumber')}
                                isInvalid={errorHelper(formik, 'identificationNumber').error}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errorHelper(formik, 'identificationNumber').helperText}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formBasicVatId">
                            <Form.Label>DIČ</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Zadajte DIČ"
                                {...formik.getFieldProps('vatId')}
                                isInvalid={errorHelper(formik, 'vatId').error}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errorHelper(formik, 'vatId').helperText}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formBasicVatRegNo">
                            <Form.Label>DIČ DPH</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Zadajte DIČ DPH"
                                {...formik.getFieldProps('vatRegNo')}
                                isInvalid={errorHelper(formik, 'vatRegNo').error}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errorHelper(formik, 'vatRegNo').helperText}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>

                    <Col style={{ paddingTop: isAbroad ? 0 : '1.85rem' }}>
                        <Form.Group className="mb-3" controlId="formBasicStreet">
                            <Form.Label>Ulica*</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Ulica a číslo"
                                {...formik.getFieldProps('address.street')}
                                isInvalid={formik.touched.address?.street && formik.errors.address?.street}
                            />
                            <Form.Control.Feedback type="invalid">
                                {formik.errors.address?.street}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formBasicZip">
                            <Form.Label>PSČ*</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={isAbroad ? 'PSČ / ZIP' : '00000'}
                                {...formik.getFieldProps('address.zip')}
                                isInvalid={formik.touched.address?.zip && formik.errors.address?.zip}
                            />
                            <Form.Control.Feedback type="invalid">
                                {formik.errors.address?.zip}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formBasicCity">
                            <Form.Label>Mesto*</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Mesto"
                                {...formik.getFieldProps('address.city')}
                                isInvalid={formik.touched.address?.city && formik.errors.address?.city}
                            />
                            <Form.Control.Feedback type="invalid">
                                {formik.errors.address?.city}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formBasicCountry">
                            <Form.Label>Krajina*</Form.Label>
                            {isAbroad ? (
                                <>
                                    <CreatableSelect
                                        options={COUNTRIES}
                                        value={formik.values.address.country ? { value: formik.values.address.country, label: formik.values.address.country } : null}
                                        onChange={option => formik.setFieldValue('address.country', option ? option.value : '')}
                                        onBlur={() => formik.setFieldTouched('address.country', true)}
                                        placeholder="Vyhľadať alebo vybrať krajinu..."
                                        formatCreateLabel={val => `Použiť: "${val}"`}
                                        isClearable
                                        noOptionsMessage={() => 'Žiadne výsledky'}
                                        styles={{
                                            control: (base, state) => ({
                                                ...base,
                                                borderColor: formik.touched.address?.country && formik.errors.address?.country ? '#dc3545' : state.isFocused ? '#86b7fe' : '#ced4da',
                                                boxShadow: formik.touched.address?.country && formik.errors.address?.country ? '0 0 0 0.25rem rgba(220,53,69,.25)' : state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,.25)' : 'none',
                                                '&:hover': { borderColor: formik.touched.address?.country && formik.errors.address?.country ? '#dc3545' : '#86b7fe' }
                                            })
                                        }}
                                    />
                                    {formik.touched.address?.country && formik.errors.address?.country && (
                                        <div className="invalid-feedback d-block">
                                            {formik.errors.address?.country}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <Form.Control
                                    type="text"
                                    placeholder="Slovensko"
                                    {...formik.getFieldProps('address.country')}
                                    isInvalid={formik.touched.address?.country && formik.errors.address?.country}
                                />
                            )}
                            {!isAbroad && (
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.address?.country}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <hr className="mt-2 mb-3" />
                <p className="fw-semibold mb-3">Kontaktná osoba</p>
                <Row>
                    <Col>
                        <Form.Group className="mb-3" controlId="formBasicContact">
                            <Form.Label>Telefón</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={isAbroad ? '+XXXXXXXXXXX' : '+421XXXXXXXXX'}
                                {...formik.getFieldProps('contact')}
                                isInvalid={errorHelper(formik, 'contact').error}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errorHelper(formik, 'contact').helperText}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group className="mb-3" controlId="formBasicEmail">
                            <Form.Label>E-mail</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Zadajte e-mail"
                                {...formik.getFieldProps('email')}
                                isInvalid={errorHelper(formik, 'email').error}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errorHelper(formik, 'email').helperText}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row style={{ position: 'sticky', bottom: 0, background: '#fff', paddingTop: '10px' }}>
                    <hr />
                    <Col className="d-flex justify-content-end">
                        <Button
                            className="button-custom-large"
                            variant="primary"
                            type="submit"
                            size="lg"
                        >
                            Pridať odberateľa/dodávateľa
                        </Button>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default AddClient;
